import { useEffect, useState } from 'react';

export type AppAuthMaintenance = {
  enabled: boolean;
  reason: string;
  loaded: boolean;
};

export type AppSiteMaintenance = {
  enabled: boolean;
  reason: string;
  startAt?: string | null;
  etaAt?: string | null;
  betaBypass: boolean;
  loaded: boolean;
};

export type AppSettingsClient = {
  auth: AppAuthMaintenance;
  site: AppSiteMaintenance;
};

export function useAppSettings(): AppSettingsClient {
  const [auth, setAuth] = useState<AppAuthMaintenance>({
    enabled: false,
    reason: '',
    loaded: false,
  });
  const [site, setSite] = useState<AppSiteMaintenance>({
    enabled: false,
    reason: '',
    startAt: null,
    etaAt: null,
    betaBypass: false,
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await fetch('/api/admin/app-settings');
        if (!cancelled && res.ok) {
          const json = await res.json();
          setAuth({
            enabled: Boolean(json?.authMaintenanceEnabled),
            reason: json?.authMaintenanceReason || '',
            loaded: true,
          });
          setSite({
            enabled: Boolean(json?.siteMaintenanceEnabled),
            reason: json?.siteMaintenanceReason || '',
            startAt: json?.siteMaintenanceStartAt || null,
            etaAt: json?.siteMaintenanceEtaAt || null,
            betaBypass: Boolean(json?.betaMaintenanceBypass),
            loaded: true,
          });
        } else if (!cancelled) {
          setAuth((s) => ({ ...s, loaded: true }));
          setSite((s) => ({ ...s, loaded: true }));
        }
      } catch {
        if (!cancelled) {
          setAuth((s) => ({ ...s, loaded: true }));
          setSite((s) => ({ ...s, loaded: true }));
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { auth, site };
}

export function useAuthMaintenance(): AppAuthMaintenance {
  const settings = useAppSettings();
  return settings.auth;
}
